import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import { FileModal, PrepareModal } from '../components/ComplianceModals';
import ImportModal from '../components/ImportModal';
import { useCompany } from '../context/CompanyContext';
import { fyLabel } from '../utils/fy';
import api from '../api';
import './Dashboard.css';
import './Calendar.css';

const PURPOSE = {
  'AOC-4': 'Annual financial statements filing',
  'MGT-7': 'Annual return filing',
  'DIR-3 KYC': 'Director KYC annual',
  'DPT-3': 'Deposits return',
  'ADT-1': 'Auditor appointment',
};

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + 'T00:00:00');
  return Math.round((due - today) / 86400000);
}

function classify(item) {
  if (item.is_applicable === false) return 'not_applicable';
  if (item.status === 'filed') return 'filed';
  if (item.status === 'in_progress') return 'in_progress';
  if (item.status === 'overdue') return 'overdue';
  const days = daysUntil(item.due_date);
  if (days <= 30) return 'soon';
  return 'future';
}

function RocMca() {
  const { selectedCompanyId, selectedCompany, fyStartYear } = useCompany();
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalItem, setModalItem] = useState(null);
  const [filing, setFiling] = useState(false);
  const [prepareItem, setPrepareItem] = useState(null);
  const [startingProgress, setStartingProgress] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => {
    if (!selectedCompanyId) return;
    setLoading(true);
    api
      .get(`/compliance-items/${selectedCompanyId}`, {
        params: { fy_year: fyStartYear, category: 'ROC' },
      })
      .then((res) => {
        setItems(res.data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [selectedCompanyId, fyStartYear]);

  const applicableItems = items.filter((it) => it.is_applicable !== false);
  const filedOnTime = items.filter((it) => it.status === 'filed').length;

  const confirmFile = (payload) => {
    if (!modalItem) return undefined;
    setFiling(true);
    return api
      .post(`/compliance/${modalItem.id}/file`, payload)
      .then(() => {
        setModalItem(null);
        load();
      })
      .finally(() => setFiling(false));
  };

  const clearFile = () => {
    if (!modalItem) return undefined;
    setFiling(true);
    return api
      .post(`/compliance/${modalItem.id}/unfile`, {})
      .then(() => {
        setModalItem(null);
        load();
      })
      .finally(() => setFiling(false));
  };

  const confirmStartProgress = () => {
    if (!prepareItem) return;
    setStartingProgress(true);
    api
      .post(`/compliance/${prepareItem.id}/start`, {})
      .then(() => {
        setPrepareItem(null);
        load();
      })
      .finally(() => setStartingProgress(false));
  };

  return (
    <PageLayout>
      <div className="page-title-row">
        <div>
          <h1 className="page-title">ROC / MCA Filings</h1>
          <p className="page-subtitle">Companies Act 2013 — not covered in any other ERP — {fyLabel(fyStartYear)}</p>
        </div>
        <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportOpen(true)}>
          Import Filings
        </button>
      </div>

      {error && <p className="dash-error">Backend unreachable: {error}</p>}

      <div className={loading ? 'dash-refreshing' : ''}>
      <div className="dash-stat-grid dash-stat-grid-3">
        <div className="dash-stat-card">
          <div className="dash-stat-value dash-serif">{applicableItems.length}</div>
          <div className="dash-stat-label">Forms Due {fyLabel(fyStartYear)}</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-value dash-serif green">{filedOnTime}</div>
          <div className="dash-stat-label">Filed on Time</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-value dash-serif red">₹100/day</div>
          <div className="dash-stat-label">Penalty Risk</div>
        </div>
      </div>

      <div className="dash-card">
        <h2 className="dash-card-title">Annual Filing Tracker</h2>
        <table className="dash-table">
          <thead>
            <tr>
              <th>Form</th>
              <th>Purpose</th>
              <th>Due Date</th>
              <th>Filed On</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="dash-empty">No ROC filings for this company.</td>
              </tr>
            )}
            {items.map((item) => {
              const bucket = classify(item);
              return (
                <tr key={item.id} style={bucket === 'not_applicable' ? { opacity: 0.55 } : undefined}>
                  <td>{item.form_name}</td>
                  <td>{PURPOSE[item.form_name] || item.description}</td>
                  <td className={bucket === 'overdue' ? 'red' : bucket === 'soon' ? 'amber' : ''}>
                    {formatDate(item.due_date)}
                  </td>
                  <td>{item.status === 'filed' ? formatDate(item.date_of_filing) : '—'}</td>
                  <td>
                    {bucket === 'not_applicable' && <span className="dash-pill dash-pill-blue">N/A</span>}
                    {bucket === 'overdue' && <span className="dash-pill dash-pill-red">Overdue</span>}
                    {bucket === 'soon' && <span className="dash-pill dash-pill-amber">Due soon</span>}
                    {bucket === 'future' && <span className="dash-pill dash-pill-blue">Upcoming</span>}
                    {bucket === 'in_progress' && <span className="dash-pill dash-pill-inprogress">In Progress</span>}
                    {bucket === 'filed' && <span className="dash-pill dash-pill-green">Filed ✓</span>}
                  </td>
                  <td>
                    {(bucket === 'overdue' || bucket === 'in_progress') && (
                      <button
                        className="dash-btn dash-btn-orange"
                        type="button"
                        onClick={() => setModalItem(item)}
                      >
                        File Now
                      </button>
                    )}
                    {(bucket === 'soon' || bucket === 'future') && (
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => setPrepareItem(item)}>
                        Prepare
                      </button>
                    )}
                    {bucket === 'filed' && (
                      <button className="dash-btn dash-btn-green" type="button" onClick={() => setModalItem(item)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="dash-card">
        <h2 className="dash-card-title">MGT-7 — Auto-prefill from company data</h2>
        <div className="field-row">
          <div className="field-group">
            <span className="field-label">CIN</span>
            <input className="field-input" value={selectedCompany?.cin || ''} readOnly />
          </div>
          <div className="field-group">
            <span className="field-label">Registered State</span>
            <input className="field-input" value={selectedCompany?.state || ''} readOnly />
          </div>
          <div className="field-group">
            <span className="field-label">Financial Year</span>
            <input className="field-input" value={`${fyStartYear}-${String((fyStartYear + 1) % 100).padStart(2, '0')}`} readOnly />
          </div>
          <div className="field-group">
            <span className="field-label">Company Type</span>
            <input className="field-input" value={selectedCompany?.company_type || ''} readOnly />
          </div>
          <div className="field-group">
            <span className="field-label">Paid-up Capital</span>
            <input className="field-input" value="₹10,00,000" readOnly />
          </div>
          <div className="field-group">
            <span className="field-label">No. of Shareholders</span>
            <input className="field-input" value="4" readOnly />
          </div>
        </div>
        <div className="dash-btn-row">
          <button className="dash-btn dash-btn-orange" type="button">Save &amp; Continue</button>
          <button className="dash-btn dash-btn-outline" type="button">Auto-fill from Odoo</button>
          <button className="dash-btn dash-btn-outline" type="button">Ask AI ↗</button>
        </div>
      </div>
      </div>

      <FileModal item={modalItem} onClose={() => setModalItem(null)} onConfirm={confirmFile} onClear={clearFile} busy={filing} />

      <PrepareModal
        item={prepareItem}
        onClose={() => setPrepareItem(null)}
        onMarkInProgress={confirmStartProgress}
        busy={startingProgress}
      />
      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="roc"
        moduleLabel="ROC/MCA"
        companyId={selectedCompanyId}
        onImported={load}
      />
    </PageLayout>
  );
}

export default RocMca;
